import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

export const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
        <p className="text-gray-600 mb-8">Last Updated: March 19, 2026</p>

        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using the services of Next Generation Tree Service & Landscaping LLC ("Company," "we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services Provided</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We provide professional tree care and landscaping services, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Tree removal and crane removals</li>
              <li>Tree trimming and pruning</li>
              <li>Emergency tree services (24/7 availability)</li>
              <li>Technical tree removals</li>
              <li>Stump grinding</li>
              <li>Landscaping services</li>
              <li>Arbor care and maintenance</li>
              <li>Lawn care and grass cutting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Estimates and Quotes</h2>
            <p className="text-gray-700 leading-relaxed">
              All estimates and quotes provided are approximate and subject to change based on actual job conditions. Final pricing will be determined after an on-site assessment. We reserve the right to adjust pricing if unforeseen circumstances or additional work is required.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Scheduling and Cancellations</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Scheduling</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Service appointments are scheduled based on availability. We will make reasonable efforts to accommodate your preferred date and time but cannot guarantee specific scheduling.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Cancellations</h3>
            <p className="text-gray-700 leading-relaxed">
              If you need to cancel or reschedule an appointment, please provide at least 24 hours notice. Cancellations with less than 24 hours notice may be subject to a cancellation fee.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Payment is due upon completion of services unless other arrangements have been made</li>
              <li>We accept cash, check, and major credit cards</li>
              <li>A deposit may be required for large projects</li>
              <li>Late payments may be subject to interest charges</li>
              <li>All prices are in US Dollars</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Property Access and Safety</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By scheduling our services, you grant us permission to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Access your property to perform the agreed-upon services</li>
              <li>Bring necessary equipment and vehicles onto your property</li>
              <li>Remove debris and materials related to the work</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You are responsible for ensuring that pets are secured and that any obstacles or hazards on the property are disclosed to us before work begins.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Liability and Insurance</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Next Generation Tree Service & Landscaping LLC is fully licensed, insured, and bonded. We maintain:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>General liability insurance</li>
              <li>Workers' compensation insurance</li>
              <li>All required business licenses and permits</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We are not liable for damage to underground utilities, irrigation systems, or other hidden structures that are not properly marked or disclosed. We are not responsible for pre-existing tree or plant diseases, pest infestations, or natural deterioration.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Weather and Delays</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to postpone or reschedule services due to inclement weather, unsafe conditions, or other circumstances beyond our control. We will make reasonable efforts to notify you of any delays or rescheduling.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Warranties and Guarantees</h2>
            <p className="text-gray-700 leading-relaxed">
              We guarantee that all work will be performed in a professional and workmanlike manner using industry-standard practices. However, we cannot guarantee the survival or health of trees and plants, as these are living organisms affected by many factors beyond our control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Emergency Services</h2>
            <p className="text-gray-700 leading-relaxed">
              Our 24/7 emergency tree services are available for urgent situations involving immediate safety hazards. Emergency service rates may be higher than standard rates due to the urgent nature and potential after-hours response.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on our website, including text, graphics, logos, and images, is the property of Next Generation Tree Service & Landscaping LLC and is protected by copyright and trademark laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, Next Generation Tree Service & Landscaping LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Dispute Resolution</h2>
            <p className="text-gray-700 leading-relaxed">
              Any disputes arising from these Terms of Service or our services shall be resolved through good faith negotiation. If a resolution cannot be reached, disputes shall be governed by the laws of the State of Tennessee.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services constitutes acceptance of any changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms of Service is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-2">Next Generation Tree Service & Landscaping LLC</p>
              <p className="text-gray-700">Email: nextgenerationnash@gmail.com</p>
              <p className="text-gray-700">Phone: (615) 944-9032 / (615) 310-4173</p>
              <p className="text-gray-700">Hours: Open 24 hours (Emergency Services Available)</p>
              <p className="text-gray-700">Location: Nashville, Tennessee & Surrounding Areas</p>
            </div>
          </section>

          <section className="border-t pt-6">
            <p className="text-gray-600 text-sm italic">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
